import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  LayoutDashboard,
  Building2,
  UserCog,
  Inbox,
  TrendingUp,
  CalendarRange,
  FileBarChart,
  ClipboardList,
  CalendarCheck,
  Network,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  BookOpen,
  CalendarDays,
  Layers,
  NotebookText,
  Video,
  Users,
  BarChart3,
  SlidersHorizontal,
  Repeat,
  ArrowLeftRight,
  Award,
  Bell,
  Wallet,
  Settings as SettingsIcon,
  Boxes,
  GraduationCap,
  HeartHandshake,
  FileSignature,
  ShieldCheck,
  Briefcase,
  Hash,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { OrgPortalApi } from '@/services/org.api';
import { cn } from '@/lib/cn';
import type { LucideIcon } from 'lucide-react';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  comingSoon?: boolean;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

interface NavGroup {
  title: string;
  /** Icon shown on the always-visible left rail — every group is reachable
   * directly from the rail now (no "pick a module from /org/modules
   * first" gating; that picker page was removed). */
  icon: LucideIcon;
  sections: NavSection[];
}

/** Same public, pre-auth-safe endpoint the superadmin login page already uses (see PublicService.branding()) — org admins have no permission to the authenticated `/platform/settings/general` endpoint, so this narrow subset (name/logo/theme color) is the only branding source available here. */
async function fetchBranding() {
  const { data } = await axios.get('/api/v1/public/branding');
  return (data?.data ?? data) as { name: string; themeColor: string; horizontalLogoUrl: string | null; verticalLogoUrl: string | null };
}

/** Academic work is per-school — deep-link into the existing "enter a
 * school" flow (OrgSelectSchoolPage), landing on the target school-tier
 * screen instead of /dashboard. Same flow as the "Enter a school" button on
 * Overview — not a new pattern, and no school-tier screen is duplicated here. */
function enterSchool(path: string): string {
  return `/org/select-school?next=${encodeURIComponent(path)}`;
}

const groups: NavGroup[] = [
  {
    title: 'Overview',
    icon: LayoutDashboard,
    sections: [
      {
        items: [{ to: '/org', label: 'Overview', icon: LayoutDashboard, end: true }],
      },
    ],
  },
  {
    // Module 3 — Schools & Admission. Staff Management/Affidavit/Class
    // Change Request belong to OTHER modules (built later) — deliberately
    // not here.
    title: 'Schools & Admission',
    icon: Building2,
    sections: [
      {
        label: 'Schools',
        items: [
          { to: '/org/schools', label: 'Schools', icon: Building2 },
          { to: '/org/admin-users', label: 'Admin Users', icon: UserCog },
        ],
      },
      {
        items: [
          { to: '/org/online-admission/applicants', label: 'Online Admission', icon: Inbox },
          { to: '/org/online-admission/summary', label: 'Admission Summary', icon: BarChart3 },
          { to: '/org/online-admission/classes', label: 'Manage Admission Classes', icon: SlidersHorizontal },
          { to: '/org/session-years', label: 'Session Years', icon: CalendarRange },
        ],
      },
      {
        // 'Student Admission' is deliberately NOT here — admitting a
        // student is a school-tier action (per-school form, reached by
        // entering that school), not an org-tier one; only school admin
        // logins get that nav item. 'Student Details'/'Student Portfolio'
        // relink the already-built cross-school report/portfolio screens
        // rather than duplicating them.
        label: 'Students',
        items: [
          { to: '/org/reports/students', label: 'Student Details', icon: Users },
          { to: '/org/reports/students/portfolio', label: 'Student Portfolio', icon: Users },
          { to: '/org/students/guardians', label: 'Guardians', icon: Users },
          { to: '/org/students/pgc-registrations', label: 'PGC Registrations', icon: HeartHandshake },
        ],
      },
      {
        items: [{ to: '/org/affidavit', label: 'Affidavit', icon: FileSignature }],
      },
      {
        // Real legacy nests Promotion under schools_admission
        // (`activeModule == 'schools_admission'`), not Academic.
        label: 'Promotion',
        items: [
          { to: '/org/promotion/promote-student', label: 'Promote Student', icon: TrendingUp },
          { to: '/org/promotion/bulk', label: 'Bulk Promotion', icon: Repeat },
          { to: '/org/promotion/report', label: 'Promotion Report', icon: TrendingUp },
        ],
      },
      {
        // 'Class Change Request' is one of the 8 real top-level
        // Schools & Admission items (same screenshot survey that added
        // Students/Affidavit above) — moved here from Academic, where it
        // was previously mis-grouped. 'Teacher Class Change Requests' is
        // a related extension of the same feature, kept alongside it.
        label: 'Class Change Requests',
        items: [
          { to: '/org/class-change-requests', label: 'Class Change Requests', icon: ArrowLeftRight },
          { to: '/org/teacher-class-change-requests', label: 'Teacher Class Change Requests', icon: ArrowLeftRight },
        ],
      },
      {
        // The last of the 8 real top-level Schools & Admission items.
        // 'Role & Permission'/'Staff' reuse the already-working school-tier
        // RolesService/StaffService; 'Staff Leave'/'Leave Report' are a
        // fresh web-admin surface over the real (already-migrated)
        // leaves/leave_masters/leave_details tables — see
        // docs/LEAVE-MANAGEMENT-REAL-REFERENCE.md.
        label: 'Staff Management',
        items: [
          { to: '/org/staff-management/staff', label: 'Staff', icon: UserCog },
          { to: '/org/staff-management/roles', label: 'Role & Permission', icon: ShieldCheck },
          { to: '/org/staff-management/staff-leave', label: 'Staff Leave', icon: CalendarCheck },
          { to: '/org/staff-management/leave-report', label: 'Leave Report', icon: FileBarChart },
        ],
      },
    ],
  },
  {
    // Module 4 — Academic. Almost entirely school-tier (per-school
    // structural setup/attendance/timetable — no org-wide aggregate makes
    // sense beyond what the dashboard already shows, see docs/DEVIATIONS.md).
    // Most of these items enter a chosen school and land on its existing
    // screen — they do NOT duplicate any school-tier page at org level.
    // 'Co-scholastic Assessment' is the deliberate exception: a genuinely
    // NEW org-tier screen (school picked inline, loops across many
    // schools in one request) reusing the SAME underlying school-tier
    // service rather than duplicating the write path. The rest are dead
    // legacy data (never migrated) — nav stubs only, not built.
    title: 'Academic',
    icon: GraduationCap,
    sections: [
      {
        items: [
          { to: '/org/academic-calendar', label: 'Academic Calendar', icon: CalendarDays },
          { to: '/org/co-scholastic', label: 'Co-scholastic Assessment', icon: Award },
          { to: '/org/reports/departments', label: 'Departments', icon: Layers },
          // Both now have full create/edit/delete directly on the org-tier
          // page itself (see OrgLessonsPage.tsx/OrgLessonTopicsPage.tsx) —
          // no "enter a school" deep-link needed, unlike earlier in this
          // session when they were read-only reports.
          { to: '/org/reports/lessons', label: 'Lessons', icon: NotebookText },
          { to: '/org/reports/lesson-topics', label: 'Lesson Topics', icon: NotebookText },
        ],
      },
    ],
  },
  {
    title: 'Quran Tracker',
    icon: BookOpen,
    sections: [
      {
        items: [
          { to: '/org/quran-tracker/targets', label: 'Quran Targets', icon: BookOpen },
          { to: '/org/quran-tracker/student-targets', label: 'Student Quran Targets', icon: GraduationCap },
        ],
      },
    ],
  },
  {
    title: 'Notification',
    icon: Bell,
    sections: [
      {
        // Both are per-school content in real legacy (a slider/notification
        // belongs to one school, no org-wide aggregate) — but real legacy
        // itself has NO "enter a school" step for either: a Super Admin
        // session there just sees the rows for whichever school's data it
        // touches, inline on one page (confirmed by reading the real
        // SliderController/NotificationController directly — scopeOwner()
        // auto-filters by the logged-in user's own school_id, with no
        // separate school-selection UI at all). These two links go to
        // proper org-tier screens with an inline school dropdown instead
        // of the old enterSchool() full-session-switch flow, the closest
        // achievable match given our schema-per-tenant architecture.
        items: [
          { to: '/org/sliders', label: 'Sliders', icon: Layers },
          { to: '/org/notifications', label: 'Notifications', icon: Bell },
        ],
      },
    ],
  },
  {
    title: 'Fees',
    icon: Wallet,
    sections: [
      {
        // Manage Fees is genuinely a per-school transactional screen, but —
        // matching real legacy's own Super-Admin pattern (a plain
        // cross-school screen with an inline School field, not a
        // session-switch) — is built as a real org-tier page here
        // (FeesPage with scope="organization") rather than an "enter
        // school" link. School Summary and Head Office Fees are the
        // pre-existing real cross-school superadmin screens. 'Fees Type'
        // is a plain per-school label list (never referenced by a real FK
        // from fee calculations) — its own cross-school screen (school
        // picker + "apply to all schools" create), matching the real
        // source's own "Apply to: All Schools" admin capability.
        items: [
          { to: '/org/ho-fees-share', label: 'Head Office Fees Share', icon: Wallet, end: true },
          { to: '/org/fees/school-summary', label: 'School Summary', icon: Wallet },
          { to: '/org/fees/head-office-overview', label: 'Head Office Fees', icon: Wallet },
          { to: '/org/fees/type', label: 'Fees Type', icon: Wallet },
          { to: '/org/fees/manage', label: 'Manage Fees', icon: Wallet },
        ],
      },
      {
        label: 'Noorani',
        items: [
          { to: '/org/fees/noorani', label: 'Noorani Fees', icon: Wallet },
          { to: '/org/fees/noorani/report', label: 'Noorani Fee Report', icon: Wallet },
        ],
      },
    ],
  },
  {
    // Sidebar lists just the 5 categories (matching
    // OrgReportsDashboardPage.tsx's CATEGORIES) — picking one shows that
    // category's reports on the right via /org/reports/category/:slug,
    // rather than flattening every individual report into the sidebar.
    title: 'Reports',
    icon: FileBarChart,
    sections: [
      {
        items: [
          { to: '/org/reports/category/student', label: 'Student Reports', icon: GraduationCap, end: true },
          { to: '/org/reports/category/attendance', label: 'Attendance Reports', icon: CalendarCheck },
          { to: '/org/reports/category/teacher', label: 'Teacher Reports', icon: Briefcase },
          { to: '/org/reports/category/general', label: 'General Reports', icon: BarChart3 },
          { to: '/org/reports/category/operations', label: 'Operations & Data', icon: Layers },
        ],
      },
    ],
  },
  {
    title: 'Noorani Management',
    icon: Video,
    sections: [
      {
        items: [
          { to: '/org/noorani-groups', label: 'Noorani Groups', icon: Users },
          { to: '/org/noorani-attendance', label: 'Noorani Attendance Report', icon: CalendarCheck, end: true },
          { to: '/org/noorani-attendance/teachers', label: 'Noorani Teacher Attendance', icon: CalendarCheck },
          { to: '/org/noorani-tutorials', label: 'Noorani Tutorials', icon: Video },
        ],
      },
    ],
  },
  {
    title: 'Material Management',
    icon: Boxes,
    sections: [
      {
        items: [
          { to: '/org/material-requests', label: 'Material Requests', icon: Boxes, end: true },
          { to: '/org/material-items', label: 'Material Items Master', icon: Layers },
        ],
      },
    ],
  },
  {
    title: 'Program Management',
    icon: ClipboardList,
    sections: [
      {
        items: [
          { to: '/org/program-dashboard', label: 'Program Dashboard', icon: ClipboardList, end: true },
          { to: '/org/program-items', label: 'Program Item', icon: ClipboardList },
          { to: '/org/chest-number-ranges', label: 'Chest Number Ranges', icon: Hash },
        ],
      },
    ],
  },
  {
    // Pinned last on the rail — same "always reachable, but not competing
    // for top-of-list attention" placement as a settings icon everywhere.
    title: 'Settings',
    icon: SettingsIcon,
    sections: [
      {
        items: [
          { to: '/org/settings', label: 'Settings', icon: SettingsIcon, end: true },
          { to: '/org/admission-limits', label: 'Admission Limits', icon: CalendarRange },
          { to: '/org/mobile-api-docs', label: 'Mobile API Docs', icon: BookOpen },
        ],
      },
    ],
  },
];

// `NavLink`'s own `isActive` matches on pathname only, ignoring the query
// string — breaks for sibling items that share a pathname and differ
// only by query (e.g. 'Schools' at /org/schools vs 'Create School' at
// /org/schools?new=1: both lit up together on either one). Pre-index, per
// pathname, every query string a sibling item uses there — once, at
// module scope (`groups` never changes) — so a bare item (no query of
// its own) can be excluded from active state exactly when the current
// search matches one of ITS siblings' specific query, without breaking
// its normal active-on-this-page highlighting for any other, unrelated
// query string (pagination, search, etc.).
const siblingQueriesByPath = new Map<string, Set<string>>();
for (const item of groups.flatMap((g) => g.sections).flatMap((s) => s.items)) {
  if (!item.to.includes('?')) continue;
  const [path, query] = item.to.split('?');
  const set = siblingQueriesByPath.get(path) ?? new Set<string>();
  set.add(query);
  siblingQueriesByPath.set(path, set);
}

function isNavItemActive(to: string, location: { pathname: string; search: string }, end?: boolean): boolean {
  const [itemPath, itemQuery] = to.split('?');
  const pathMatches = end ? location.pathname === itemPath : location.pathname.startsWith(itemPath);
  if (!pathMatches) return false;
  if (itemQuery) return location.search === `?${itemQuery}`;
  const siblingQueries = siblingQueriesByPath.get(itemPath);
  return !siblingQueries || !siblingQueries.has(location.search.slice(1));
}

/** The group's own landing page — where a rail-icon click navigates to. */
function firstItemPath(group: NavGroup): string {
  for (const section of group.sections) {
    if (section.items.length) return section.items[0].to;
  }
  return '/org';
}

/** Which group "owns" the current URL — longest matching item path wins
 * (same tie-break as isNavItemActive), used to auto-select the right rail
 * icon/panel on load, refresh, or browser back/forward. */
function groupForPath(pathname: string): NavGroup | undefined {
  let best: { group: NavGroup; len: number } | undefined;
  for (const group of groups) {
    for (const section of group.sections) {
      for (const item of section.items) {
        const itemPath = item.to.split('?')[0];
        // Must mirror isNavItemActive's `end` handling: without it, the
        // Overview item (to: '/org', end: true) prefix-matches as a
        // catch-all for literally every /org/* path (startsWith('/org/')
        // is true for all of them), always winning by default over the
        // Reports fallback below for any path with no specific nav entry
        // anywhere.
        const matches = item.end ? pathname === itemPath : pathname === itemPath || pathname.startsWith(itemPath + '/');
        if (matches) {
          if (!best || itemPath.length > best.len) best = { group, len: itemPath.length };
        }
      }
    }
  }
  if (best) return best.group;
  // Some report-hub destinations (e.g. Student Summary Report) aren't
  // independently registered under any sidebar group's own nav items —
  // they're only reachable via a card on the Reports hub. Without this,
  // such a path matches nothing above and falls back to the single-item
  // "Overview" group, which hides the flyout panel entirely. Any
  // otherwise-unmatched /org/reports/* path still conceptually belongs to
  // Reports.
  if (pathname.startsWith('/org/reports')) return groups.find((g) => g.title === 'Reports');
  return undefined;
}

export function OrgLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore((s) => s.logout);
  const [panelOpen, setPanelOpen] = useState(true);
  const [activeGroupTitle, setActiveGroupTitle] = useState(() => groupForPath(location.pathname)?.title ?? groups[0].title);
  const { data: org } = useQuery({ queryKey: ['org-me'], queryFn: OrgPortalApi.me });
  const { data: branding } = useQuery({ queryKey: ['public-branding'], queryFn: fetchBranding, staleTime: 5 * 60 * 1000 });
  const logoUrl = branding?.verticalLogoUrl ?? branding?.horizontalLogoUrl ?? null;

  // Keep the active rail icon/panel in sync with direct navigation (deep
  // links, browser back/forward, a sub-item link inside the panel itself)
  // — not just clicks on the rail. Some destination pages are reused by
  // more than one sidebar group (e.g. Student Portfolio/Online Admission
  // are real nav items under Schools & Admission but are also linked from
  // a Reports-hub card) — groupForPath's longest-match-wins can only ever
  // pick one "owner", which broke "entered via Reports" navigation.
  // OrgReportsDashboardPage passes { orgNavGroup: 'Reports' } in router
  // state on its card links specifically to disambiguate that case; it
  // takes priority over the generic path-based lookup, which still governs
  // everything else (direct links, refreshes, the rail itself).
  //
  // Deliberately keyed on pathname ALONE, not [pathname, state]: several
  // destination pages (e.g. OrgOnlineAdmissionsPage) normalize their
  // filters into the URL via setSearchParams right on mount, which is a
  // same-pathname history.replace that React Router does NOT carry
  // location.state through (state becomes null) unless explicitly passed.
  // That replace fires a split second after the Reports-card navigation
  // lands, so re-running this on every state change re-evaluated
  // groupForPath with the marker already gone and flipped back to Schools
  // & Admission. The group choice should only be (re)decided the moment
  // the pathname itself changes, not re-litigated by a same-page
  // setSearchParams call.
  useEffect(() => {
    const stateGroupTitle = (location.state as { orgNavGroup?: string } | null)?.orgNavGroup;
    if (stateGroupTitle && groups.some((g) => g.title === stateGroupTitle)) {
      setActiveGroupTitle(stateGroupTitle);
      return;
    }
    const group = groupForPath(location.pathname);
    if (group) setActiveGroupTitle(group.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const activeGroup = groups.find((g) => g.title === activeGroupTitle) ?? groups[0];
  // A group with exactly one item (e.g. "Overview") has nothing left to
  // disambiguate once its rail icon is clicked — showing a flyout panel
  // with that same single link is pure redundancy, not real navigation.
  const activeGroupItemCount = activeGroup.sections.reduce((n, s) => n + s.items.length, 0);
  const showPanel = panelOpen && activeGroupItemCount > 1;

  const handleLogout = () => {
    logout();
    navigate('/org/login');
  };

  function selectGroup(group: NavGroup) {
    setActiveGroupTitle(group.title);
    setPanelOpen(true);
    navigate(firstItemPath(group));
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Icon rail — every category is always reachable here, no "pick a
          module first" gating (the old /org/modules picker was removed).
          Light theme (white, not the old bg-slate-900 black) with a
          labeled icon per group, matching a light CRM-style rail — wider
          than the old icon-only 64px rail to fit the label underneath. */}
      <aside className="flex w-20 shrink-0 flex-col items-center border-r border-slate-200 bg-white py-4">
        {/* Brand badge — the org's own logo once set in General Settings
            (platform tier → /platform/settings/general), falling back to
            the generic Network icon until one is uploaded. Deliberately
            NOT the active-nav-icon color, which would read as "always
            selected". */}
        <div
          className="mb-4 flex h-9 w-9 items-center justify-center overflow-hidden rounded-md bg-slate-100 ring-1 ring-slate-200"
          title={org?.name ?? 'Organization'}
        >
          {logoUrl ? (
            <img src={logoUrl} alt={branding?.name ?? 'Logo'} className="h-full w-full object-contain" />
          ) : (
            <Network className="h-5 w-5 text-brand-600" />
          )}
        </div>

        <nav className="flex flex-1 flex-col items-center gap-1 overflow-y-auto">
          {groups.map((group) => {
            const Icon = group.icon;
            const isActive = group.title === activeGroupTitle;
            return (
              <button
                key={group.title}
                type="button"
                onClick={() => selectGroup(group)}
                title={group.title}
                className={cn(
                  'flex w-16 shrink-0 flex-col items-center gap-0.5 rounded-md px-1 py-1.5 transition',
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900',
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="w-full truncate text-center text-[9px] font-medium leading-tight">{group.title}</span>
              </button>
            );
          })}
        </nav>

        {activeGroupItemCount > 1 && (
          <button
            type="button"
            onClick={() => setPanelOpen((v) => !v)}
            className="mt-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            title={panelOpen ? 'Hide panel' : 'Show panel'}
          >
            {panelOpen ? <ChevronsLeft className="h-4 w-4" /> : <ChevronsRight className="h-4 w-4" />}
          </button>
        )}
      </aside>

      {/* Flyout panel — the active group's own items, grouped the same way
          the old always-expanded sidebar was. Skipped entirely for a
          single-item group (e.g. Overview) — see showPanel above. */}
      {showPanel && (
        <aside className="flex w-64 shrink-0 flex-col overflow-y-auto border-r border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-4">
            <div className="text-sm font-semibold text-slate-900">{activeGroup.title}</div>
          </div>
          <nav className="flex-1 px-3 py-3">
            {activeGroup.sections.map((section, sIdx) => (
              <div key={section.label ?? sIdx} className={sIdx > 0 ? 'mt-3' : undefined}>
                {section.label && (
                  <div className="mb-1 px-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    {section.label}
                  </div>
                )}
                <ul className="space-y-0.5">
                  {section.items.map(({ to, label, icon: Icon, end, comingSoon }) => (
                    <li key={to}>
                      <NavLink
                        to={to}
                        end={end}
                        className={() =>
                          cn(
                            'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition',
                            isNavItemActive(to, location, end)
                              ? 'bg-indigo-50 text-indigo-700'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                          )
                        }
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="flex flex-1 items-center justify-between gap-2">
                          {label}
                          {comingSoon && (
                            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-500">
                              Soon
                            </span>
                          )}
                        </span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between border-b border-slate-200 bg-white px-6">
          <div className="leading-tight">
            <div className="text-sm font-semibold text-slate-800">{org?.name ?? 'Organization'}</div>
            <div className="text-xs text-slate-500">Organization admin</div>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 rounded-md p-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-red-600"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </header>

        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
